import { useEffect, useMemo } from "react";

import { config } from "@/config";
import { LicensePriceObjectFields } from "@/context/SUIQuery/SuiQueryContext.types";
import { useSuiQueryContext } from "@/context/SUIQuery/useSuiQueryContext";
import { useSuiObject } from "@/hooks/sui/queries";
import currency from "@/lib/currency";

import { LicenseSaleTreasuryProps } from "./LicenseSaleTreasury.types";

// @TESTNET
// const PHASE1_END = 12;
// const PHASE2_END = 16;
// const PHASE3_END = 21;
// const PHASE4_END = 25;
// const PHASE5_END = 30;

// @MAINNET
const PHASE1_END = 12000;
const PHASE2_END = 16500;
const PHASE3_END = 21000;
const PHASE4_END = 25500;
const PHASE5_END = 30000;

const formatter = new Intl.NumberFormat("en-US");

const calculateSalesToNextTier = (totalDistribution: number) => {
  // Assigned in every branch below, including the final `else`.
  let currentTier: string;
  let salesToNextTier: number;

  if (totalDistribution > PHASE5_END) {
    currentTier = "Tier 6";
    salesToNextTier = 0;
  } else if (totalDistribution > PHASE4_END) {
    currentTier = "Tier 5";
    salesToNextTier = PHASE5_END - totalDistribution;
  } else if (totalDistribution > PHASE3_END) {
    currentTier = "Tier 4";
    salesToNextTier = PHASE4_END - totalDistribution;
  } else if (totalDistribution > PHASE2_END) {
    currentTier = "Tier 3";
    salesToNextTier = PHASE3_END - totalDistribution;
  } else if (totalDistribution > PHASE1_END) {
    currentTier = "Tier 2";
    salesToNextTier = PHASE2_END - totalDistribution;
  } else {
    currentTier = "Tier 1";
    salesToNextTier = PHASE1_END - totalDistribution;
  }

  return {
    currentTier,
    salesToNextTier,
  };
};

export const LicenseSaleTreasury: React.FC<LicenseSaleTreasuryProps> = () => {
  const { setTreasury, transactionOutput } = useSuiQueryContext();

  const { data, isPending, error, isFetched, refetch } = useSuiObject(config.treasuryObjectId);

  const salesData = useMemo(() => {
    if (!data?.json) return undefined;

    // MIGRATION: gRPC returns Move struct contents as FLAT JSON — the JSON-RPC
    // `fields.price.current` nesting is gone, it is now
    // `json.price.current`.
    const fields = data.json as unknown as {
      sales: string;
      airdrops: string;
      total_distribution: string;
      price: { current: string; next: string };
      balance: string;
      is_purchasing_paused: boolean;
      wallet: string;
      holders: { size: string };
    };

    const totalDistribution = fields?.total_distribution;

    if (!totalDistribution) return undefined;

    const { currentTier, salesToNextTier } = calculateSalesToNextTier(Number(totalDistribution));

    const currentPrice = fields.price.current;
    const nextPrice = fields.price.next;

    const price: LicensePriceObjectFields = {
      current: {
        raw: BigInt(currentPrice),
        text: currentPrice,
        formatted: `${currency.convert.toDecimalsPrecisionString(currentPrice, 6)} ${currency.constants.DEFAULT_CURRENCY_TICKER}`,
      },
      next: {
        raw: BigInt(nextPrice),
        text: nextPrice,
        formatted: `${currency.convert.toDecimalsPrecisionString(nextPrice, 6)} ${currency.constants.DEFAULT_CURRENCY_TICKER}`,
      },
    };

    const balance = {
      raw: BigInt(fields.balance),
      text: fields.balance,
      formatted: `${currency.convert.toDecimalsPrecisionString(fields.balance, 6)} ${currency.constants.DEFAULT_CURRENCY_TICKER}`,
    };

    const sales = fields.sales;
    const airdrops = fields.airdrops;

    return {
      sales,
      currentTier,
      salesToNextTier: formatter.format(salesToNextTier),
      price,
      balance,
      airdrops,
      totalDistribution,
      isPurchasingPaused: fields.is_purchasing_paused,
      wallet: fields.wallet,
      holders: fields.holders.size,
    };
  }, [data?.json]);

  useEffect(() => {
    if (salesData) {
      setTreasury(salesData);
    }
  }, [salesData, setTreasury]);

  useEffect(() => {
    if (!isFetched) return;

    refetch();
  }, [isFetched, transactionOutput.purchaseLicense.onSuccess?.digest]);

  if (error) {
    return null;
  }

  if (isPending) {
    return null;
  }

  return null;
};
